<template>
  <div
    v-bind="mainAttrs"
    class="progress-circular"
    :class="classes"
    :style="styles"
  >
    <svg v-bind="svgAttrs">
      <defs>
        <filter
          :id="`underlay-shadow-${_uid}`"
          x="-100%"
          y="-100%"
          width="300%"
          height="300%"
        >
          <feGaussianBlur in="FillPaint" stdDeviation="1.4" />
          <feOffset dx="0" dy="0" result="offsetblur" />
          <feFlood flood-color="#000" flood-opacity="0.5" />
          <feComposite in2="offsetblur" operator="in" />
          <feMerge>
            <feMergeNode />
            <feMergeNode in="FillPaint" />
          </feMerge>
        </filter>

        <filter
          :id="`circle-shadow-${_uid}`"
          x="-150%"
          y="-150%"
          width="400%"
          height="400%"
        >
          <feGaussianBlur in="FillPaint" stdDeviation="1.4" />
          <feOffset dx="0" dy="0" result="offsetblur" />
          <feFlood flood-color="currentColor" flood-opacity="0.8" />
          <feComposite in2="offsetblur" operator="in" />
          <feMerge>
            <feMergeNode />
            <feMergeNode in="FillPaint" />
          </feMerge>
        </filter>

        <mask v-if="dashedMask" :id="`mask-dasharray-${_uid}`">
          <rect x="0%" y="0%" width="200%" height="200%" fill="black" />
          <circle
            v-if="!indeterminate"
            v-bind="circleAttrs"
            stroke="white"
            fill="black"
            :stroke-dasharray="dashedMask"
          />
        </mask>

        <mask :id="`mask-circles-${_uid}`">
          <rect x="0%" y="0%" width="200%" height="200%" fill="black" />
          <path
            :d="maskPath"
            stroke="white"
            fill="black"
            :stroke-width="strokeWidth * 5"
          />
        </mask>
      </defs>

      <g
        :transform="(rotate && setTransform([transformRotate(rotate)])) || null"
      >
        <path
          v-if="!indeterminate"
          :d="maskPath"
          :stroke-width="strokeWidth"
          :style="{ transform: transformScale(10) }"
          :filter="`url(#underlay-shadow-${_uid})`"
          class="progress-circular__underlay"
        />
        <g :style="maskStyles">
          <circle
            v-if="!indeterminate"
            v-bind="circleAttrs"
            class="progress-circular__underlay"
            :stroke-dashoffset="strokeOffsetUnderlay"
            :mask="`url(#mask-dasharray-${_uid})`"
          />
          <circle
            v-bind="circleAttrs"
            class="progress-circular__overlay"
            stroke-linecap="round"
            :stroke-dashoffset="strokeOffsetOverlay"
            :mask="`url(#mask-dasharray-${_uid})`"
          />
          <circle
            v-if="!indeterminate"
            v-bind="glowCircleData"
            class="progress-circular__glow"
          />
        </g>
      </g>
    </svg>
    <div class="progress-circular__info">
      <slot></slot>
    </div>
  </div>
</template>

<script>
import "./ProgressCircular.scss";
import { convertToUnit, polarToCartesian, describeArc } from "@utils/helpers";

export default {
  name: "ProgressCircular",

  props: {
    button: Boolean,
    indeterminate: Boolean,

    colorBreakpoints: {
      type: Object,
      default: () => ({})
    },

    rotate: {
      type: [Number, String],
      default: 0
    },

    size: {
      type: [Number, String],
      default: 32
    },

    offset: {
      type: [Number, String],
      default: 360
    },

    width: {
      type: [Number, String],
      default: 4
    },

    value: {
      type: [Number, String],
      default: 0
    },

    dashedMask: {
      type: String,
      default: null
    }
  },

  data: () => ({
    radius: 20
  }),

  computed: {
    circumference() {
      return 2 * Math.PI * this.radius;
    },

    normalizedValue() {
      if (this.value < 0) {
        return 0;
      }

      if (this.value > 100) {
        return 100;
      }

      return parseFloat(this.value);
    },

    calculatedSize() {
      return Number(this.size) + (this.button ? 8 : 0);
    },

    maskStyles() {
      if (this.indeterminate || +this.offset === 360) return {};

      return {
        mask: `url(#mask-circles-${this._uid})`
      };
    },

    mainAttrs() {
      return {
        role: "progressbar",
        "aria-valuemin": 0,
        "aria-valuemax": 100,
        "aria-valuenow": this.indeterminate ? undefined : this.normalizedValue
      };
    },

    classes() {
      let list = {
        "progress-circular_indeterminate": this.indeterminate,
        "progress-circular_button": this.button
      };

      Object.keys(this.colorBreakpoints)
        .filter(key => this.colorBreakpoints[key] >= this.normalizedValue)
        .forEach((name, i) => {
          list[name] = i === 0;
        });

      return list;
    },

    styles() {
      return {
        height: convertToUnit(this.calculatedSize),
        width: convertToUnit(this.calculatedSize)
      };
    },

    strokeDashArray() {
      return Math.round(this.circumference * 1000) / 1000;
    },

    strokeWidth() {
      return (+this.width / +this.size) * this.boxSize * 2;
    },

    boxSize() {
      return this.radius / (1 - +this.width / +this.size);
    },

    svgAttrs() {
      return {
        xmlns: "http://www.w3.org/2000/svg",
        viewBox: `${this.boxSize} ${this.boxSize} ${2 * this.boxSize} ${2 *
          this.boxSize}`,
        style: {
          overflow: "visible"
        }
      };
    },

    circleAttrs() {
      return {
        cx: 2 * this.boxSize,
        cy: 2 * this.boxSize,
        r: this.radius,
        "stroke-width": this.strokeWidth,
        "stroke-dasharray": this.strokeDashArray
      };
    },

    strokeOffsetUnderlay() {
      return this.strokeDashOffset(100) + "px";
    },
    strokeOffsetOverlay() {
      return this.strokeDashOffset(this.normalizedValue) + "px";
    },

    maskPath() {
      return describeArc(
        this.boxSize * 2,
        this.boxSize * 2,
        20,
        0,
        this.offset - 0
      );
    },

    glowCircleData() {
      let valueOffset = 1 - this.getOffsetPart(this.normalizedValue) + 1,
        point = polarToCartesian(this.boxSize * 2, this.boxSize * 2, 20, 0),
        angle = (360 * valueOffset) % 360;

      return {
        cx: point.x,
        cy: point.y,
        r: this.strokeWidth / 2,
        opacity: this.normalizedValue > 0 && this.normalizedValue < 100 ? 1 : 0,
        filter: `url(#circle-shadow-${this._uid})`,
        transform: this.setTransform([
          this.transformRotate(angle, this.boxSize, this.boxSize)
        ])
      };
    }
  },

  methods: {
    setTransform(list) {
      return ((Array.isArray(list) && list) || [list]).join(" ");
    },

    transformScale(num) {
      return `translate(${num / 2}%, ${num / 2}%) scale(${(100 - num) / 100})`;
    },

    transformRotate(a, x = null, y = null) {
      let list = [a, x, y].filter(n => n !== null);
      return `rotate(${list.join(" ")})`;
    },

    getOffsetPart(num) {
      let offsetPart = this.offset / 360;
      return ((100 - num) / 100) * offsetPart + (1 - offsetPart);
    },

    strokeDashOffset(val) {
      return this.getOffsetPart(val) * this.circumference;
    }
  }
};
</script>
